import {
    AllocationDestinationDto,
    AllocationRuleDto,
    JourneyResponse,
    OccupancyType,
    YardBlockDto,
} from '@yard/app/api'
import { OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'
import { getSlotCountByDestination } from './yard-block.util'

export interface ScopeCapacity {
  totalTeu: number
  consumedTeu: number
  utilizationPct: number
  isOvercapacity: boolean
  isWarning: boolean
}

export interface RuleCapacity {
  totalTeu: number
  occupiedTeu: number
  reservedTeu: number
  plannedTeu: number
  freeTeu: number
  ruleSizeTeu: number
  available: number
  utilizationPct: number
  consumedTeu: number
  isOvercapacity: boolean
  isWarning: boolean
}

export interface RuleCapacityContext {
  yardBlocks: YardBlockDto[]
  occupanciesByBlock: Record<string, OcuppancyWithFilterData[]>
  journeysByRuleId: Record<string, JourneyResponse[]>
}

const containerTeu = (length: number | null | undefined): number => (length === 40 ? 2 : 1)

const ruleSizeTeu = (rule: AllocationRuleDto): number => (rule.facets?.size === 20 ? 1 : 2)

const positionInDestination = (
  position: { bay: number; row: number } | null | undefined,
  destination: AllocationDestinationDto,
): boolean => {
  if (!position) return false
  return (
    position.bay >= destination.minBay &&
    position.bay <= destination.maxBay &&
    position.row >= destination.minRow &&
    position.row <= destination.maxRow
  )
}

const collectOccupiedJourneyIds = (
  occupanciesByBlock: Record<string, OcuppancyWithFilterData[]>,
): Set<number> => {
  const ids = new Set<number>()
  Object.values(occupanciesByBlock).forEach(occupancies =>
    occupancies.forEach(o => ids.add(o.journeyId)),
  )
  return ids
}

export const getRuleCapacity = (
  rule: AllocationRuleDto,
  ctx: RuleCapacityContext,
  warningThreshold?: number | null,
): RuleCapacity => {
  const occupiedJourneyIds = collectOccupiedJourneyIds(ctx.occupanciesByBlock)

  let totalTeu = 0
  let occupiedTeu = 0
  let reservedTeu = 0

  rule.destinations.forEach(destination => {
    totalTeu += getSlotCountByDestination(
      ctx.yardBlocks,
      destination.blockId,
      destination.minBay,
      destination.maxBay,
      destination.minRow,
      destination.maxRow,
    )

    const occupancies = ctx.occupanciesByBlock[destination.blockId] ?? []
    occupancies.forEach(occ => {
      if (!positionInDestination(occ.position, destination)) return
      const teu = containerTeu(occ.length)
      if (occ.type === OccupancyType.Reserved) {
        reservedTeu += teu
      } else {
        // Default undefined or Occupied type to Occupied (design.md: "Frontend code defaults
        // missing `type` to `Occupied`").
        occupiedTeu += teu
      }
    })
  })

  const ruleJourneys = ctx.journeysByRuleId[rule.id] ?? []
  const plannedTeu = ruleJourneys
    .filter(journey => !occupiedJourneyIds.has(journey.id))
    .reduce((sum, journey) => sum + containerTeu(journey.container?.length), 0)

  const consumedTeu = occupiedTeu + reservedTeu + plannedTeu
  const freeTeu = Math.max(0, totalTeu - consumedTeu)
  const sizeTeu = ruleSizeTeu(rule)
  const available = Math.floor(freeTeu / sizeTeu)
  const utilizationPct = totalTeu === 0 ? 0 : Math.round((consumedTeu / totalTeu) * 100)

  const isOvercapacity = consumedTeu > 0 && (totalTeu === 0 || utilizationPct >= 100)
  const isWarning = computeIsWarning(utilizationPct, isOvercapacity, warningThreshold)

  return {
    totalTeu,
    occupiedTeu,
    reservedTeu,
    plannedTeu,
    freeTeu,
    ruleSizeTeu: sizeTeu,
    available,
    utilizationPct,
    consumedTeu,
    isOvercapacity,
    isWarning,
  }
}

const computeIsWarning = (
  utilizationPct: number,
  isOvercapacity: boolean,
  warningThreshold: number | null | undefined,
): boolean =>
  warningThreshold !== null &&
  warningThreshold !== undefined &&
  utilizationPct >= warningThreshold &&
  !isOvercapacity

const consumedTeuFromOccupancies = (occupancies: OcuppancyWithFilterData[]): number =>
  occupancies.reduce((sum, occ) => sum + containerTeu(occ.length), 0)

const pctAndState = (
  consumedTeu: number,
  totalTeu: number,
  warningThreshold: number | null | undefined,
) => {
  const utilizationPct = totalTeu === 0 ? 0 : Math.round((consumedTeu / totalTeu) * 100)
  const isOvercapacity = utilizationPct >= 100
  const isWarning = computeIsWarning(utilizationPct, isOvercapacity, warningThreshold)
  return {
    utilizationPct,
    isOvercapacity: consumedTeu > 0 && (totalTeu === 0 || utilizationPct >= 100),
    isWarning,
  }
}

export const getBlockCapacity = (
  block: YardBlockDto,
  occupanciesInBlock: OcuppancyWithFilterData[],
  warningThreshold?: number | null,
): ScopeCapacity => {
  const totalTeu = getSlotCountByDestination([block], block.id, 1, block.maxBays, 1, block.maxRows)
  const consumedTeu = consumedTeuFromOccupancies(occupanciesInBlock)
  return { totalTeu, consumedTeu, ...pctAndState(consumedTeu, totalTeu, warningThreshold) }
}

export const getYardCapacity = (
  yardBlocks: YardBlockDto[],
  occupanciesByBlock: Record<string, OcuppancyWithFilterData[]>,
  warningThreshold?: number | null,
): ScopeCapacity => {
  const totalTeu = yardBlocks.reduce(
    (sum, block) =>
      sum + getSlotCountByDestination([block], block.id, 1, block.maxBays, 1, block.maxRows),
    0,
  )
  const consumedTeu = Object.values(occupanciesByBlock).reduce(
    (sum, occupancies) => sum + consumedTeuFromOccupancies(occupancies),
    0,
  )
  return { totalTeu, consumedTeu, ...pctAndState(consumedTeu, totalTeu, warningThreshold) }
}
